/**
 * t25-cart-discount.js
 * Custom Element to handle Cart Discount logic without external dependencies.
 * Implements apply, remove, and error handling for discount codes.
 */

if (!customElements.get('t25-cart-discount-component')) {
  class T25CartDiscount extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.onApplyDiscount = this.applyDiscount.bind(this);
      this.onRemoveDiscount = this.removeDiscount.bind(this);
    }

    connectedCallback() {
      // Defer execution to ensure children are parsed
      setTimeout(() => {
        // Changed from form submit to explicit button/input handling
        this.input = this.querySelector('input[name="t25_discount_input"]');
        this.applyBtn = this.querySelector('.t25-cart-discount__button');
        
        this.onEnterKey = (e) => {
            if (e.key === 'Enter') {
            e.preventDefault(); // Prevent outer form submit if any
            this.applyDiscount(e);
            }
        };

        if (this.input) {
            this.input.addEventListener('keydown', this.onEnterKey);
        } else {
            console.warn('t25-cart-discount: input not found');
        }
        
        if (this.applyBtn) {
            this.applyBtn.addEventListener('click', this.onApplyDiscount);
        } else {
            console.warn('t25-cart-discount: apply button not found');
        }

        // Delegated click for remove buttons
        // We can attach this to 'this' immediately as it bubbles, but safe to keep here.
        this.removeEventListener('click', this.handleRemove); // Cleanup old
        this.handleRemove = (event) => {
            const removeBtn = event.target.closest('.t25-cart-discount__pill-remove');
            if (removeBtn) {
                this.onRemoveDiscount(event);
            }
        };
        this.addEventListener('click', this.handleRemove);

        this.cartDiscountError = this.querySelector('[data-ref="cartDiscountError"]');
        this.cartDiscountErrorDiscountCode = this.querySelector('[data-ref="cartDiscountErrorDiscountCode"]');
        this.cartDiscountErrorShipping = this.querySelector('[data-ref="cartDiscountErrorShipping"]');
      }, 0);
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('keydown', this.onEnterKey);
      }
      if (this.applyBtn) {
        this.applyBtn.removeEventListener('click', this.onApplyDiscount);
      }
    }

    createAbortController() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      return this.abortController;
    }

    async applyDiscount(event) {
      event.preventDefault();
      event.stopPropagation();

      const input = this.querySelector('input[name="t25_discount_input"]');
      if (!input) return;

      const discountCodeValue = input.value.trim();
      if (!discountCodeValue) return;

      if (!this.dataset.sectionId) {
        console.error('t25-cart-discount: Missing data-section-id attribute');
        return;
      }

      const existingDiscounts = this.existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        return;
      }

      this.hideErrors();

      const button = this.querySelector('.t25-cart-discount__button');
      let originalButtonText = '';
      if (button) {
        originalButtonText = button.textContent;
        button.disabled = true;
        button.textContent = '...';
      }

      const abortController = this.createAbortController();
      const routes = window.routes || {};
      const cartUpdateUrl = routes.cart_update_url || '/cart/update.js';

      try {
        // Method 1: Try Standard Storefront API
        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        });

        const response = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: body,
          signal: abortController.signal,
        });

        const data = await response.json();
        
        if (!response.ok) throw new Error('Network error');

        // Check if discount was actually applied
        // We look at the section HTML to see if the pill exists, OR we check logic if possible.
        // Since we are replacing the section anyway, let's look at the NEW HTML.
        const newHtml = data.sections[this.dataset.sectionId];
        
        let success = false;
        if (newHtml) {
            // Temporary parser to check for the code in the new HTML
            const doc = new DOMParser().parseFromString(newHtml, 'text/html');
            const pills = doc.querySelectorAll('.t25-cart-discount__pill');
            const codes = Array.from(pills).map(p => p.dataset.discountCode);
            
            if (codes.includes(discountCodeValue)) {
                success = true;
                // Pass full data object so we can dispatch events with cart data
                this.replaceSection(newHtml, data);
                input.value = '';
            }
        }

        // Method 2: Fallback to /discount/[code] if Method 1 failed
        if (!success) {
            await this.forceApplyDiscount(discountCodeValue, existingDiscounts);
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.showError('discount_code');
          const details = this.closest('details');
          if (details) details.open = true;
        }
      } finally {
        this.abortController = null;
        if (button) {
          button.disabled = false;
          button.textContent = originalButtonText;
        }
      }
    }

    async forceApplyDiscount(code, existingDiscounts) {
        const input = this.querySelector('input[name="t25_discount_input"]');
        // This endpoint sets a cookie. We assume it redirects or returns 200.
        try {
            // Cache busting for the discount endpoint to ensure fresh request
            // Use encodeURIComponent to safely encode special characters in the discount code
            await fetch(`/discount/${encodeURIComponent(code)}?t=${Date.now()}`);
            
            // Now we must reload the cart section to reflect the change
            // We use sections API to specific section
            const routes = window.routes || {};
            const rootUrl = routes.root_url || routes.rootUrl || '/';
            const cleanRoot = rootUrl === '/' ? '' : rootUrl;
            // Fetch updated section
            const sectionUrl = `${cleanRoot}/cart?sections=${this.dataset.sectionId}`;
            
            const response = await fetch(sectionUrl);
            const data = await response.json();
            
            const newHtml = data[this.dataset.sectionId];
            if (newHtml) {
                // Check success again
                const doc = new DOMParser().parseFromString(newHtml, 'text/html');
                const pills = doc.querySelectorAll('.t25-cart-discount__pill');
                const codes = Array.from(pills).map(p => p.dataset.discountCode);

                if (codes.includes(code)) {
                    // Success
                    // We don't have the full cart object here easily without another call, 
                    // so we pass null/empty for data and rely on cart:refresh to update everything else.
                    this.replaceSection(newHtml, null);
                    input.value = '';
                } else {
                    // Still failed
                    this.showError('discount_code');
                    const details = this.closest('details');
                    if(details) details.open = true;
                }
            }
        } catch(e) {
            console.error('Fallback failed', e);
            this.showError('discount_code');
            const details = this.closest('details');
            if(details) details.open = true;
        }
    }

    async removeDiscount(event) {
      event.preventDefault();
      event.stopPropagation();

      const button = event.target.closest('.t25-cart-discount__pill-remove');
      const pill = button.closest('.t25-cart-discount__pill');
      if (!pill) return;

      const discountCode = pill.dataset.discountCode;
      const existingDiscounts = this.existingDiscounts();
      const newDiscounts = existingDiscounts.filter(code => code !== discountCode);

      const abortController = this.createAbortController();
      const routes = window.routes || {};
      const cartUpdateUrl = routes.cart_update_url || '/cart/update.js';

      // Visual feedback on removal
      pill.style.opacity = '0.5';

      try {
        const body = JSON.stringify({
          discount: newDiscounts.join(','),
          sections: [this.dataset.sectionId],
        });

        const response = await fetch(cartUpdateUrl, {
           method: 'POST',
           headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
           body: body,
           signal: abortController.signal
        });

        const data = await response.json();
        if (data.sections && data.sections[this.dataset.sectionId]) {
            this.replaceSection(data.sections[this.dataset.sectionId], data);
        }

      } catch (error) {
         console.error(error);
         pill.style.opacity = '1';
      } finally {
         this.abortController = null;
      }
    }

    replaceSection(htmlString, cartData) {
      const doc = new DOMParser().parseFromString(htmlString, 'text/html');
      const sectionElement = document.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
      const newSectionElement = doc.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
      
      if (sectionElement && newSectionElement) {
          // Replace the entire section to avoid nested section markup
          sectionElement.replaceWith(newSectionElement);
          
          this.fixCartLayout(newSectionElement);
          
          // Re-initialize theme JS components (like sticky sidebar)
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: {
              sectionId: this.dataset.sectionId
            }
          }));

          // Determine which events to fire
          // 'cart:rerendered' is used by CartSection to re-attach listeners and fix height.
          // 'cart:refresh' is used by Header Cart to update bubble/drawer.
          
          if (cartData) {
               document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartData } }));
               document.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true, detail: cartData }));
          } else {
               // Fallback or generic event
               document.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true, detail: {} }));
          }
          
          // Always fire refresh to ensure global components (Header, MiniCart) stay in sync.
          // Note: This might cause a double-fetch of the cart section in some themes, but is necessary for header consistency.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }

    existingDiscounts() {
      const pills = this.querySelectorAll('.t25-cart-discount__pill');
      return Array.from(pills).map(pill => pill.dataset.discountCode).filter(Boolean);
    }

    showError(type) {
      if (this.cartDiscountError) {
          this.cartDiscountError.style.display = 'flex';
          this.cartDiscountError.classList.remove('hidden');
      }
      if (type === 'discount_code' && this.cartDiscountErrorDiscountCode) {
          this.cartDiscountErrorDiscountCode.style.display = 'block';
          this.cartDiscountErrorDiscountCode.classList.remove('hidden');
      } else if (type === 'shipping' && this.cartDiscountErrorShipping) {
          this.cartDiscountErrorShipping.style.display = 'block';
          this.cartDiscountErrorShipping.classList.remove('hidden');
      }
    }
    
    hideErrors() {
        if (this.cartDiscountError) {
            this.cartDiscountError.style.display = 'none';
            this.cartDiscountError.classList.add('hidden');
        }
        if (this.cartDiscountErrorDiscountCode) {
            this.cartDiscountErrorDiscountCode.style.display = 'none';
             this.cartDiscountErrorDiscountCode.classList.add('hidden');
        }
        if (this.cartDiscountErrorShipping) {
            this.cartDiscountErrorShipping.style.display = 'none';
            this.cartDiscountErrorShipping.classList.add('hidden');
        }
    }

    /**
     * T25 - Fix Layout Shift
     * Manually calculate and enforce min-height on .cart-wrapper
     * This mimics the logic in theme.js CartSection._enforceMinimumHeight
     * but ensures it runs immediately after we replace the HTML.
     */
    fixCartLayout(sectionElement) {
        const updateHeight = () => {
            const cartWrapper = sectionElement.querySelector('.cart-wrapper');
            const cartRecapScroller = sectionElement.querySelector('.cart-recap__scroller');

            if (cartWrapper && cartRecapScroller) {
                const height = cartRecapScroller.clientHeight;
                if (height > 0) {
                    cartWrapper.style.minHeight = `${height}px`;
                }
            }
        };

        // 1. Immediate update
        requestAnimationFrame(updateHeight);

        // 2. Persistent update using ResizeObserver
        // This handles cases like opening "Order Instructions" or "Shipping Estimator"
        const cartRecapScroller = sectionElement.querySelector('.cart-recap__scroller');
        if (cartRecapScroller && window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(updateHeight);
            });
            resizeObserver.observe(cartRecapScroller);
        }
    }
  }

  customElements.define('t25-cart-discount-component', T25CartDiscount);
}
